// Shared types for authentication across all Airclou apps
/**
 * Default session configuration
 * Production settings: 30 minute timeout, refresh 5 min before expiry, check every 60s
 */
export const defaultSessionConfig = {
    inactivityTimeoutMinutes: 30,
    tokenRefreshBeforeExpiryMinutes: 5,
    checkIntervalSeconds: 60,
};
/**
 * Development session configuration
 * Longer timeout for development convenience
 */
export const devSessionConfig = {
    inactivityTimeoutMinutes: 120, // 2 hours
    tokenRefreshBeforeExpiryMinutes: 10,
    checkIntervalSeconds: 60,
};
/**
 * Test session configuration
 * Very short timeout for testing the inactivity logout
 */
export const testSessionConfig = {
    inactivityTimeoutMinutes: 2, // 2 minutes for quick testing
    tokenRefreshBeforeExpiryMinutes: 1,
    checkIntervalSeconds: 10,
};
/**
 * Decode a JWT token payload without signature verification (client-side only).
 * Returns null if the token is malformed.
 */
export function decodeJwtPayload(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3)
            return null;
        // JWT segments are base64url ('-' and '_', no padding); `atob` only accepts
        // standard base64 and throws on those characters. Any payload whose JSON
        // encodes non-ASCII bytes (a name like "José" or a CJK name) readily
        // produces them, and a decoder that throws there fails open everywhere this
        // is used for an identity comparison. The percent-escape round trip decodes
        // the UTF-8 bytes correctly instead of reading them as Latin-1.
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join(''));
        return JSON.parse(json);
    }
    catch {
        return null;
    }
}
/**
 * Check if a JWT token is an impersonation token.
 * Returns ImpersonationInfo if it is, null otherwise.
 */
export function getImpersonationInfo(token) {
    const payload = decodeJwtPayload(token);
    if (!payload || payload.type !== 'impersonation')
        return null;
    return {
        active: true,
        targetUserId: payload.sub || '',
        targetUserName: payload.name || '',
        targetUserEmail: payload.email || '',
        impersonatorId: payload.impersonatorId || '',
        impersonatorEmail: payload.impersonatorEmail || '',
        reason: payload.impersonationReason || '',
        readOnly: payload.readOnly === true,
        expiresAt: payload.exp || 0,
    };
}
